/**
 * Pure helpers behind the Analyses page (spec 2026-10-10-analyses-page-design
 * §2): toolbar filtering, status counts, day groups, durations and the
 * per-run split of `analysisRunFiles` into PDFs and other files. No React, no
 * i18n — the components translate what these return.
 */
import dayjs from 'dayjs';

import { ByproductType, ProcessJobStatus, type AnalysisRunFilesQuery } from '@/graphql/graphql';
import { isStationVolumesByproduct } from '@/lib/stationVolumes/byproducts';

import type { MergedRun } from './mergeRuns';
import { ANALYSES } from './registry';
import type { AnalysisRunItem } from './runs';
import type { AnalysisId } from './types';

export type AnalysisRunFileItem = AnalysisRunFilesQuery['analysisRunFiles'][number];

/** One row of the list, with the strings the search box matches against. */
export interface RunRow {
  /** One hub row: a run, or a paired comparison merged into one (`mergeRuns`). */
  run: MergedRun;
  id: AnalysisId;
  /** Translated analysis title ("Compare surfaces"). */
  title: string;
  /** Input layer names, " · "-joined. */
  inputNames: string;
  /** Translated result tags ("Cut & fill · Deviation map"), searchable; '' for
   *  an analysis without parts. */
  tags: string;
}

export type RunStatusFilter = 'all' | 'running' | 'failed';

export interface RunFilters {
  query: string;
  status: RunStatusFilter;
  /** null = every analysis type. */
  analysisId: AnalysisId | null;
}

export const EMPTY_RUN_FILTERS: RunFilters = { query: '', status: 'all', analysisId: null };

export const isFiltering = (f: RunFilters): boolean =>
  f.query.trim() !== '' || f.status !== 'all' || f.analysisId !== null;

/** "Running" for the toolbar: queued or processing, however old (a stuck run
 *  is still one the user wants to find). */
export const isRunInProgress = (run: Pick<AnalysisRunItem, 'status'>): boolean =>
  run.status === ProcessJobStatus.Waiting || run.status === ProcessJobStatus.Processing;

const matchesStatus = (run: AnalysisRunItem, status: RunStatusFilter): boolean =>
  status === 'all' ||
  (status === 'running' && isRunInProgress(run)) ||
  (status === 'failed' && run.status === ProcessJobStatus.Failed);

/** Case-insensitive; every whitespace-separated term must match the run's
 *  name, analysis title, a result tag or an input layer name. */
const matchesQuery = (row: RunRow, query: string): boolean => {
  const terms = query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  const hay = `${row.run.name ?? ''}\n${row.title}\n${row.tags}\n${row.inputNames}`.toLocaleLowerCase();
  return terms.every((term) => hay.includes(term));
};

export function filterRunRows(rows: readonly RunRow[], filters: RunFilters): RunRow[] {
  return rows.filter(
    (row) =>
      matchesStatus(row.run, filters.status) &&
      (filters.analysisId === null || row.id === filters.analysisId) &&
      matchesQuery(row, filters.query),
  );
}

/** Chip counts. They count every run (not the filtered set), so a chip never
 *  reads 0 just because another filter is active. */
export function countRunStatuses(rows: readonly RunRow[]): Record<RunStatusFilter, number> {
  let running = 0;
  let failed = 0;
  for (const { run } of rows) {
    if (isRunInProgress(run)) running++;
    else if (run.status === ProcessJobStatus.Failed) failed++;
  }
  return { all: rows.length, running, failed };
}

/** The analysis types present in the list, in registry order. */
export function analysisTypesPresent(rows: readonly RunRow[]): AnalysisId[] {
  const present = new Set(rows.map((r) => r.id));
  return ANALYSES.map((d) => d.id).filter((id) => present.has(id));
}

export const toDate = (value: unknown): Date => (value instanceof Date ? value : new Date(value as string));

export type DayKind = 'today' | 'yesterday' | 'date';

export interface RunDayGroup<T> {
  /** Local calendar day, `YYYY-MM-DD`. */
  key: string;
  kind: DayKind;
  /** Start of that local day. */
  date: Date;
  rows: T[];
}

/**
 * Groups rows by the local calendar day the run was STARTED (`createdAt`, the
 * server's sort key), keeping the incoming order — newest first in, newest
 * first out. Consecutive runs of one day share a group; an out-of-order row
 * from an earlier day still lands in that day's group.
 */
export function groupRunsByDay<T extends { run: Pick<AnalysisRunItem, 'createdAt'> }>(
  rows: readonly T[],
  nowMs: number,
): RunDayGroup<T>[] {
  const today = dayjs(nowMs).startOf('day');
  const yesterday = today.subtract(1, 'day');
  const groups = new Map<string, RunDayGroup<T>>();
  for (const row of rows) {
    const day = dayjs(toDate(row.run.createdAt)).startOf('day');
    const key = day.isValid() ? day.format('YYYY-MM-DD') : 'unknown';
    let group = groups.get(key);
    if (!group) {
      const kind: DayKind = day.isSame(today) ? 'today' : day.isSame(yesterday) ? 'yesterday' : 'date';
      group = { key, kind, date: day.toDate(), rows: [] };
      groups.set(key, group);
    }
    group.rows.push(row);
  }
  return [...groups.values()];
}

/** Wall-clock run time, or null when unfinished / unparseable / negative. */
export function runDurationMs(run: Pick<AnalysisRunItem, 'createdAt' | 'finishedAt'>): number | null {
  if (!run.finishedAt) return null;
  const ms = toDate(run.finishedAt).getTime() - toDate(run.createdAt).getTime();
  return Number.isFinite(ms) && ms >= 0 ? ms : null;
}

/** Elapsed since the run was created, clamped at 0 (clock skew). */
export function runElapsedMs(run: Pick<AnalysisRunItem, 'createdAt'>, nowMs: number): number {
  const ms = nowMs - toDate(run.createdAt).getTime();
  return Number.isFinite(ms) ? Math.max(0, ms) : 0;
}

export type DurationParts =
  | { unit: 'seconds'; n: number }
  | { unit: 'minutes'; n: number }
  | { unit: 'hours'; hours: number; minutes: number };

/** Under a minute → whole seconds; under an hour → whole minutes (floored);
 *  otherwise hours + minutes. */
export function durationParts(ms: number): DurationParts {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  if (totalSeconds < 60) return { unit: 'seconds', n: totalSeconds };
  const totalMinutes = Math.floor(totalSeconds / 60);
  if (totalMinutes < 60) return { unit: 'minutes', n: totalMinutes };
  return { unit: 'hours', hours: Math.floor(totalMinutes / 60), minutes: totalMinutes % 60 };
}

/** Human-readable size; '' when unknown. */
export function formatFileSize(bytes: number | string | null | undefined): string {
  const n = typeof bytes === 'string' ? Number(bytes) : bytes;
  if (n == null || !Number.isFinite(n) || n < 0) return '';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

export interface RunFiles {
  /** Newest first. */
  pdfs: AnalysisRunFileItem[];
  /** Every other downloadable file, newest first. */
  others: AnalysisRunFileItem[];
}

export const EMPTY_RUN_FILES: RunFiles = { pdfs: [], others: [] };

/** A file's identity: the same byproduct can belong to two runs (one report
 *  covering both), so `id` alone is not unique. */
export const runFileKey = (f: Pick<AnalysisRunFileItem, 'runId' | 'id'>): string => `${f.runId}:${f.id}`;

/**
 * Splits the flat `analysisRunFiles` list per run into PDFs and the rest,
 * newest first, de-duplicated on (runId, id). Station-volume data files are
 * dropped — the dock loads them itself; older rows predate the `isInternal`
 * flag, so the filename rule stays as the fallback (as in `ByproductList`).
 */
export function groupRunFiles(files: readonly AnalysisRunFileItem[]): Map<string, RunFiles> {
  const seen = new Set<string>();
  const byRun = new Map<string, RunFiles>();
  const sorted = [...files].sort((a, b) => toDate(b.createdAt).getTime() - toDate(a.createdAt).getTime());
  for (const f of sorted) {
    const key = runFileKey(f);
    if (seen.has(key) || isStationVolumesByproduct(f.filename)) continue;
    seen.add(key);
    let entry = byRun.get(f.runId);
    if (!entry) {
      entry = { pdfs: [], others: [] };
      byRun.set(f.runId, entry);
    }
    (f.type === ByproductType.Pdf ? entry.pdfs : entry.others).push(f);
  }
  return byRun;
}

/**
 * Query-key part for `analysisRunFiles`: sorted `id:status` of finished runs.
 * It changes exactly when a run finishes (or a finished one drops off the
 * list), which is when its files can have appeared — the query is never
 * polled.
 */
export function finishedRunsSignature(runs: readonly Pick<AnalysisRunItem, 'id' | 'status'>[] | undefined): string {
  return (runs ?? [])
    .filter((r) => r.status === ProcessJobStatus.Processed || r.status === ProcessJobStatus.Failed)
    .map((r) => `${r.id}:${r.status}`)
    .sort()
    .join(',');
}
